// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: a favorite row's preview line, "<author>: <message>" (favorites-bookmarks-plan.md sections 1.1, 1.3
// and 3). The DM row's preview (DMListItem.tsx, getMessagePreview) in its classes and markup, with two differences:
// the author is the account's display name, then its username, then "Someone" (never a Cast character or a guild
// nickname), and it is always shown, whatever the DM preview setting or the unread state. No message, no access or
// not loaded yet: no line. Exclude from upstream sync.

import dmStyles from '@app/features/channel/components/direct_message/DirectMessageList.module.css';
import type {Channel} from '@app/features/channel/models/Channel';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import {
	PREVIEW_SENT_AN_ATTACHMENT_DESCRIPTOR,
	PREVIEW_SEPARATOR,
	PREVIEW_YOU_DESCRIPTOR,
	FavoritesBookmarksStrings as S,
} from '@app/features/messaging/FavoritesBookmarksStrings';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import FavoritePreviews from '@app/features/messaging/state/FavoritePreviews';
import {SystemMessageUtils} from '@app/features/messaging/utils/SystemMessageUtils';
import Users from '@app/features/user/state/Users';
import {MessageTypes} from '@fluxer/constants/src/ChannelConstants';
import type {I18n} from '@lingui/core';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

export function previewAuthorName(message: Message, currentUserId: string | undefined, i18n: I18n): string {
	if (message.author.id === currentUserId) return i18n._(PREVIEW_YOU_DESCRIPTOR);
	return message.author.globalName || message.author.username || S.someone;
}

function previewContent(message: Message, channelId: string, i18n: I18n): React.ReactNode | null {
	const authorPrefix = `${previewAuthorName(message, Users.currentUser?.id, i18n)}${PREVIEW_SEPARATOR}`;
	if (message.type !== MessageTypes.DEFAULT && message.type !== MessageTypes.REPLY) {
		const systemText = SystemMessageUtils.stringify(message, i18n);
		return systemText ? systemText.replace(/\.$/, '') : null;
	}
	if (message.content) {
		return (
			<>
				{authorPrefix}
				<span className={dmStyles.dmItemPreviewMarkdown} data-flx="messaging.favorite-preview-line.markdown">
					<SafeMarkdown
						content={message.content}
						options={{
							context: MarkdownContext.RESTRICTED_INLINE_REPLY,
							channelId,
							messageId: message.id,
							disableAnimatedEmoji: true,
							disableInteractions: true,
							mentionChannels: message.mentionChannels,
						}}
						data-flx="messaging.favorite-preview-line.safe-markdown"
					/>
				</span>
			</>
		);
	}
	if (message.attachments?.length > 0) {
		return (
			<>
				{authorPrefix}
				<span className={dmStyles.dmItemSubtextItalic} data-flx="messaging.favorite-preview-line.attachment">
					{i18n._(PREVIEW_SENT_AN_ATTACHMENT_DESCRIPTOR)}
				</span>
			</>
		);
	}
	return null;
}

export const FavoritePreviewLine = observer(
	({channel, 'data-flx': dataFlx}: {channel: Channel; 'data-flx'?: string}) => {
		const {i18n} = useLingui();
		const preview = FavoritePreviews.get(channel.id);
		if (!preview) return null;
		const content = previewContent(preview, channel.id, i18n);
		if (content == null) return null;
		return (
			<span className={dmStyles.dmItemSubtext} data-flx={dataFlx ?? 'messaging.favorite-preview-line'}>
				{content}
			</span>
		);
	},
);
